// Редът с филтрите на „Продажби по периоди": групирането и трите чипа.
//
// Групирането е два свързани сегмента (режим → единица) вместо един дълъг
// падащ списък със седем реда — двата въпроса „слято или във времето" и
// „колко едро" са отделни. Чиповете стесняват отговора на BE-то (те са
// параметри на заявката), докато филтрите в колоните стесняват таблицата.
// Станциите не са чип, а колони в таблицата (оборот по станция).

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMenuItems } from "../../../../hooks/useMenu";
import { useUsers } from "../../../../hooks/useUsers";
import { useDiscountClients } from "../../../../hooks/useDiscounts";
import {
  SALES_BY_PERIODS_UNITS,
  type SalesByPeriodsMode,
  type SalesByPeriodsUnit,
} from "../../../../api/reportsSalesByPeriods";
import { FilterChip, type FilterChipOption } from "./FilterChip";
import {
  EMPTY_FILTERS,
  WITHOUT_CLIENT,
  hasActiveFilters,
  type ReportFilterState,
} from "./reportFilterState";

type Props = {
  mode: SalesByPeriodsMode;
  unit: SalesByPeriodsUnit;
  onModeChange: (mode: SalesByPeriodsMode) => void;
  onUnitChange: (unit: SalesByPeriodsUnit) => void;
  filters: ReportFilterState;
  onFiltersChange: (filters: ReportFilterState) => void;
};

const segmentClass = (active: boolean) =>
  `inline-flex h-[28px] items-center whitespace-nowrap rounded-[7px] px-3 text-[11.5px] font-semibold leading-none transition-colors ${
    active
      ? "bg-brand-tint text-brand-deep-ink shadow-[inset_0_0_0_1px_var(--color-brand-tint2)]"
      : "text-body hover:bg-canvas hover:text-ink"
  }`;

const SEGMENT_SHELL =
  "inline-flex h-9 items-center gap-0.5 rounded-[10px] border border-shade-3 bg-surface p-[3px] shadow-[0_1px_2px_rgba(17,17,17,0.04)]";

export const SalesByPeriodsFilters = ({
  mode,
  unit,
  onModeChange,
  onUnitChange,
  filters,
  onFiltersChange,
}: Props) => {
  const { t, i18n } = useTranslation();
  const langCode = i18n.language?.startsWith("en") ? "en" : "bg";

  const items = useMenuItems();
  const { data: users = [] } = useUsers();
  const { data: clientsData } = useDiscountClients();

  const userOptions = useMemo<FilterChipOption[]>(
    () =>
      users.map((user) => ({
        value: String(user.id),
        label: user.names || user.username,
      })),
    [users],
  );

  const clientOptions = useMemo<FilterChipOption[]>(
    () => [
      { value: WITHOUT_CLIENT, label: t("reportsPage.salesPeriodsWithoutClient") },
      ...(clientsData?.items ?? []).map((client) => ({
        value: String(client.id),
        label: client.name,
      })),
    ],
    [clientsData, t],
  );

  const itemOptions = useMemo<FilterChipOption[]>(
    () =>
      items
        .map((item) => ({
          value: String(item.id),
          label:
            item.itemLangs.find((lang) => lang.languageCode === langCode)?.name ??
            item.itemLangs[0]?.name ??
            item.slug,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [items, langCode],
  );

  const modes: SalesByPeriodsMode[] = ["grouped", "timeline"];

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <div className={SEGMENT_SHELL} role="group">
        {modes.map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={mode === candidate}
            onClick={() => onModeChange(candidate)}
            className={segmentClass(mode === candidate)}
          >
            {t(`reportsPage.salesPeriodsMode_${candidate}`)}
          </button>
        ))}
      </div>

      <div className={SEGMENT_SHELL} role="group">
        {SALES_BY_PERIODS_UNITS[mode].map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={unit === candidate}
            onClick={() => onUnitChange(candidate)}
            className={segmentClass(unit === candidate)}
          >
            {t(`reportsPage.salesPeriodsUnit_${candidate}`)}
          </button>
        ))}
      </div>

      <span className="hidden h-5 w-px bg-shade-2 sm:block" />

      <FilterChip
        allLabel={t("reportsPage.salesPeriodsAllUsers")}
        label={t("reportsPage.salesPeriodsUsers")}
        options={userOptions}
        selected={filters.userIds}
        onChange={(userIds) => onFiltersChange({ ...filters, userIds })}
      />
      <FilterChip
        allLabel={t("reportsPage.salesPeriodsAllClients")}
        label={t("reportsPage.salesPeriodsClients")}
        options={clientOptions}
        selected={filters.clientIds}
        onChange={(clientIds) => onFiltersChange({ ...filters, clientIds })}
      />
      <FilterChip
        allLabel={t("reportsPage.salesPeriodsAllItems")}
        label={t("reportsPage.salesPeriodsItems")}
        options={itemOptions}
        selected={filters.itemIds}
        onChange={(itemIds) => onFiltersChange({ ...filters, itemIds })}
      />

      {hasActiveFilters(filters) ? (
        <button
          type="button"
          onClick={() => onFiltersChange(EMPTY_FILTERS)}
          className="h-9 rounded-lg px-2.5 text-[12px] font-bold text-muted transition-colors hover:bg-shade-1 hover:text-ink"
        >
          {t("reportsPage.salesPeriodsClearFilters")}
        </button>
      ) : null}
    </div>
  );
};
